// ============================================================================
// CASE GROUP FORM MODAL — Create / Edit batch-event header (Phase A)
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { caseGroupService } from 'api/services/caseGroupService';
import type { CaseGroup, CaseGroupMode } from 'api/types/caseGroup.types';

interface FormState {
  typeCode: string;
  title: string;
  description: string;
  mode: CaseGroupMode;
  eventDate: string;
  venue: string;
  platform: string;
}

const INITIAL: FormState = {
  typeCode: 'AUCTION-VEHICLE-STD',
  title: '', description: '',
  mode: 'PHYSICAL', eventDate: '', venue: '', platform: '',
};

// First cut: only the seeded vehicle-auction type. Others (Adalath, OTS, etc.)
// surface here as their mst.tbl_case_group_type rows get seeded.
const TYPE_OPTIONS = [
  { value: 'AUCTION-VEHICLE-STD', label: 'Vehicle Auction' },
];

const MODE_OPTIONS = [
  { value: 'PHYSICAL', label: 'Physical' },
  { value: 'ONLINE', label: 'Online' },
  { value: 'HYBRID', label: 'Hybrid' },
];

// Local-timezone YYYY-MM-DD for prefilling <input type="date">. A naïve
// .slice(0, 10) on a UTC ISO string returns the UTC calendar date — wrong for
// IST users when the stored timestamp lands just before midnight IST.
const dateOnly = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.slice(0, 10);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export interface CaseGroupFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  record?: CaseGroup | null;
  onSaved: () => void;
  onClose: () => void;
}

const CaseGroupFormModal: React.FC<CaseGroupFormModalProps> = ({ isOpen, mode, record, onSaved, onClose }) => {
  const [formData, setFormData] = useState<FormState>(INITIAL);
  const recordRef = useRef(record); recordRef.current = record;
  const snapshotRef = useRef('');
  const [isSaving, setIsSaving] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    const r = recordRef.current;
    const data: FormState = r ? {
      typeCode: 'AUCTION-VEHICLE-STD', // not editable; identity already set
      title: r.title ?? '',
      description: r.description ?? '',
      mode: r.mode,
      eventDate: dateOnly(r.eventDate),
      venue: r.venue ?? '',
      platform: r.platform ?? '',
    } : { ...INITIAL };
    setFormData(data);
    snapshotRef.current = JSON.stringify(data);
  }, [isOpen]);

  const isDirty = useMemo(
    () => !!snapshotRef.current && JSON.stringify(formData) !== snapshotRef.current,
    [formData],
  );

  const handleClose = useCallback(() => { if (isDirty) setDiscardOpen(true); else onClose(); }, [isDirty, onClose]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      if (mode === 'create') {
        await caseGroupService.create({
          typeCode: formData.typeCode,
          title: formData.title || null,
          description: formData.description || null,
          mode: formData.mode,
          eventDate: formData.eventDate || null,
          venue: formData.venue || null,
          platform: formData.platform || null,
        });
        notificationService.toast.success('Case-group created.');
      } else if (record) {
        await caseGroupService.update(record.id, {
          title: formData.title || null,
          description: formData.description || null,
          mode: formData.mode,
          eventDate: formData.eventDate || null,
          venue: formData.venue || null,
          platform: formData.platform || null,
          conductingUserId: record.conductingUserId ?? null,
          conductingAgencyId: record.conductingAgencyId ?? null,
          rowVersion: record.rowVersion,
        });
        notificationService.toast.success('Case-group updated.');
      }
      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      notificationService.toast.error(err?.response?.data?.message || 'Failed to save case-group.', 6000);
    }
  }, [formData, mode, record, onSaved, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit',
          }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Saving…</span>
          </div>
        )}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{mode === 'create' ? 'New Case Group' : 'Edit Case Group'}</h2>
          <button className="modal-close" onClick={handleClose} disabled={isSaving}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          {mode === 'create' && (
            <div className="form-group">
              <SyutSelect label={<>Event Type <span className="text-danger">*</span></>} options={TYPE_OPTIONS}
                value={formData.typeCode} onChange={(v) => setFormData(p => ({ ...p, typeCode: (v as string) || 'AUCTION-VEHICLE-STD' }))}
                isClearable={false} />
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Title</label>
            <input type="text" className="form-input" value={formData.title} placeholder="e.g. Bangalore South vehicle auction — June 2026"
              onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))} disabled={isSaving} />
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <SyutSelect label={<>Mode <span className="text-danger">*</span></>} options={MODE_OPTIONS}
                value={formData.mode} onChange={(v) => setFormData(p => ({ ...p, mode: ((v as string) || 'PHYSICAL') as CaseGroupMode }))}
                isClearable={false} />
            </div>
            <div className="form-group">
              <label className="form-label">Event Date</label>
              <input type="date" className="form-input" value={formData.eventDate}
                onChange={(e) => setFormData(p => ({ ...p, eventDate: e.target.value }))} disabled={isSaving} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Venue</label>
            <input type="text" className="form-input" value={formData.venue} placeholder="Yard / venue address"
              onChange={(e) => setFormData(p => ({ ...p, venue: e.target.value }))} disabled={isSaving} />
          </div>
          {formData.mode !== 'PHYSICAL' && (
            <div className="form-group">
              <label className="form-label">Online Platform</label>
              <input type="text" className="form-input" value={formData.platform} placeholder="e.g. eAuction portal"
                onChange={(e) => setFormData(p => ({ ...p, platform: e.target.value }))} disabled={isSaving} />
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Remarks / Description</label>
            <textarea className="form-textarea" rows={2} value={formData.description}
              onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} disabled={isSaving} />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            {mode === 'create' ? 'Create' : 'Update'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={discardOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Discard them?"
        confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
};

export default CaseGroupFormModal;
